import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import ChatMessage from "../ChatMessage/ChatMessage";
import ConversationBar from "../ConversationBar/ConversationBar";
import {
    getRepliInsightsHistory,
    clearRepliInsightsHistory,
    sendRepliMessage,
    sendRepliMessageStream,
    sendRepliFileMessage
} from "../../api/chatService";
import { translations } from "../../utils/translations";
import "./RepliInsightsAssistant.css";

const RepliInsightsAssistant = ({ user }) => {
    const [isChatVisible, setIsChatVisible] = useState(false);
    const [showTooltip, setShowTooltip] = useState(true);
    const [chatHistory, setChatHistory] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const chatContainerRef = useRef(null);
    const windowRef = useRef(null);
    const abortControllerRef = useRef(null);

    const isInsightsRoute = false; // Desactivado por ahora para evitar confusión con el chat del perfil

    // Requirement: Only show for Owners with a linked Repliker
    const canShow = !!(user && user.isOwner && user.linkedReplikerURL);

    useEffect(() => {
        const timer = setTimeout(() => {
            setShowTooltip(false);
        }, 3000);
        return () => clearTimeout(timer);
    }, []);

    const currentLang = localStorage.getItem("language") || "es";
    const t = translations[currentLang]?.home || translations["es"].home;

    useEffect(() => {
        const initConversation = async () => {
            if (canShow && isInsightsRoute) {
                try {
                    const data = await getRepliInsightsHistory();
                    if (data && data.history) {
                        setChatHistory(data.history);
                    } else {
                        setChatHistory([]);
                    }
                } catch (error) {
                    console.error("Error initializing insights assistant conversation:", error);
                }
            }
        };

        if (isChatVisible && canShow) {
            initConversation();
        }
    }, [user, isChatVisible, isInsightsRoute, canShow]);

    useEffect(() => {
        if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
    }, [chatHistory, isChatVisible]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (windowRef.current && !windowRef.current.contains(event.target)) {
                setIsChatVisible(false);
            }
        };

        if (isChatVisible) {
            document.addEventListener("mousedown", handleClickOutside);
        } else {
            document.removeEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isChatVisible]);

    const handleCancel = () => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
        }
        setIsLoading(false);
    };

    const handleNewUserMessage = async (messageObject) => {
        const { text, file } = messageObject;
        if (!text.trim() && !file) return;

        setChatHistory((prev) => [
            ...prev,
            messageObject,
            { sender: "bot", text: "", thinking: true, streamStatus: "Analizando..." }
        ]);
        setIsLoading(true);

        abortControllerRef.current = new AbortController();

        try {
            if (file) {
                const response = await sendRepliFileMessage(file, text, "repli-insights");
                if (response && response.history) {
                    setChatHistory(response.history);
                } else {
                    const botText = response?.message || response?.text || "";
                    const botMessage = { sender: "bot", text: botText };
                    setChatHistory((prev) => {
                        const newHistory = prev.filter(m => !m.thinking);
                        return [...newHistory, botMessage];
                    });
                }
            } else {
                // Streaming mode for text as fallback or future use
                await sendRepliMessageStream(text, "repli-insights", {
                    onToken: (token) => {
                        setChatHistory((prev) => {
                            const prevChat = [...prev];
                            const lastMsg = { ...prevChat[prevChat.length - 1] };
                            if (lastMsg.sender === "bot") {
                                lastMsg.thinking = false;
                                lastMsg.streamStatus = null;
                                lastMsg.text = (lastMsg.text || "") + token;
                                prevChat[prevChat.length - 1] = lastMsg;
                            }
                            return prevChat;
                        });
                    },
                    onStatus: (status) => {
                        setChatHistory((prev) => {
                            const prevChat = [...prev];
                            const lastMsg = { ...prevChat[prevChat.length - 1] };
                            if (lastMsg.sender === "bot" && lastMsg.thinking) {
                                lastMsg.streamStatus = status;
                                prevChat[prevChat.length - 1] = lastMsg;
                            }
                            return prevChat;
                        });
                    },
                    onDone: (data) => {
                        if (data && data.history) {
                            setChatHistory(data.history);
                        }
                    }
                });
            }
        } catch (error) {
            if (axios.isCancel(error)) {
                // If cancelled, remove the user message and the thinking message
                setChatHistory((prev) => prev.slice(0, -2));
            } else {
                const errorMessage = { sender: "bot", text: t.errorConnect };
                // If error, replace the thinking message with an error message
                setChatHistory((prev) => [...prev.slice(0, -1), errorMessage]);
            }
        } finally {
            setIsLoading(false);
            abortControllerRef.current = null;
        }
    };

    const handleClearChat = async () => {
        if (window.confirm(t.confirmClearChat || "¿Estás seguro de que quieres limpiar la conversación?")) {
            try {
                const response = await clearRepliInsightsHistory();
                if (response && response.history) {
                    setChatHistory(response.history);
                } else {
                    setChatHistory([]);
                }
            } catch (error) {
                console.error("Error clearing insights chat history:", error);
            }
        }
    };

    if (!isInsightsRoute || !canShow) return null;

    return (
        <div className={`repli-insights-assistant-floating ${isChatVisible ? "active" : ""}`}>
            {isChatVisible && (
                <div className="insights-chat-window" ref={windowRef}>
                    <div className="insights-chat-header">
                        <div className="header-left">
                            <div className="insights-badge-icon">
                                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" height="18" width="18">
                                    <path d="M21 21H3V3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M7 14L11 10L15 14L21 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </div>
                            <span>Repli Insights</span>
                        </div>
                        <div className="header-right">
                            <button className="clean-btn" onClick={handleClearChat} title="Limpiar">
                                <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="#fff"><path d="M280-120q-33 0-56.5-23.5T200-200v-520h-40v-80h200v-40h240v40h200v80h-40v520q0 33-23.5 56.5T720-120H280Zm440-600H240v520q0 8 6 14t14 6h440q8 0 14-6t6-14v-520ZM360-280h80v-360h-80v360Zm160 0h80v-360h-80v360ZM240-720v520-520Z" /></svg>
                            </button>
                            <button className="close-btn" onClick={() => setIsChatVisible(false)}>
                                <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="#fff"><path d="m256-200-56-56 224-224-224-224 56-56 224 224 224-224 56 56-224 224 224 224-56 56-224-224-224 224Z" /></svg>
                            </button>
                        </div>
                    </div>
                    <div className="insights-chat-messages" ref={chatContainerRef}>
                        {chatHistory.length === 0 && (
                            <div className="empty-chat-msg">{t.analysisReady || "Tu asistente de datos está listo. ¿Qué quieres analizar hoy?"}</div>
                        )}
                        {chatHistory
                            .filter((msg) => msg && msg.sender)
                            .map((msg, index) => (
                                <ChatMessage
                                    key={index}
                                    sender={msg.sender}
                                    text={msg.text}
                                    file={msg.file}
                                />
                            ))}
                        {isLoading && (
                            <ChatMessage
                                sender="bot"
                                text={t.thinking}
                                thinking={true}
                            />
                        )}
                    </div>
                    <div className="insights-chat-footer">
                        <ConversationBar
                            onNewUserMessage={handleNewUserMessage}
                            onCancel={handleCancel}
                            isLoading={isLoading}
                            selectedFile={selectedFile}
                            setSelectedFile={setSelectedFile}
                            placeholder={t.analysisPlaceholder || "Ej: ¿Qué tipo de contenido generó más comentarios este mes?"}
                        />
                    </div>
                </div>
            )}

            {!isChatVisible && (
                <button
                    className="insights-floating-trigger-btn"
                    onClick={() => setIsChatVisible(true)}
                    title="Repli Insights"
                >
                    {showTooltip && (
                        <div className="insights-tooltip">
                            Analizar mi rendimiento
                        </div>
                    )}
                    <div className="insights-trigger-icon">
                        <img
                            src="/InsightRepli.ico"
                            alt="Insight Assistant"
                            className="insights-trigger-icon-img"
                        />
                    </div>
                    <span className="insights-notification-dot"></span>
                </button>
            )}
        </div>
    );
};

export default RepliInsightsAssistant;
